import AppLoader from '@nxsys/components/AppLoader';
import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppMenuFilter from '@nxsys/components/AppMenuFilter';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import TaxYearSelect from '@nxsys/components/TaxYearSelect';
import { PAYMENT_MODEL_ENUM } from '@nxsys/constants/AppEnums';
import { useSubmitPensionContributionReport } from '@nxsys/hooks/api/report';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { reportActions } from '@nxsys/store/slices/report/reducer/reportSlice';
import { GetPendingPensionContributionReportListParams } from '@nxsys/store/slices/report/types';
import { getTaxWeekTaxYear } from '@nxsys/utils';
import { showMessage } from '@nxsys/utils/message';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PensionContributionFilterModal, {
  PensionContributionFilterValues,
} from '../../reports/pension/components/PensionContributionFilterModal';
import { getPendingPensionContributionReportColumns } from '../../reports/pension/listing/columns';
import AppButton from '@nxsys/components/AppButton';

type Props = {
  isOpen: boolean;
  toggle: () => void;
  cbAfterAction: () => void;
};

const PendingPensionSubmissionModal: React.FC<Props> = ({
  isOpen,
  toggle,
  cbAfterAction,
}) => {
  const { messages } = useTypedIntl();

  const dispatch = useDispatch();
  const { pendingPensionContributionReportList } = useSelector(
    (state: AppState) => state.reportStore,
  );
  const { list, pagination, loading, queryParams } =
    pendingPensionContributionReportList;
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);
  const currentTaxYear = getTaxWeekTaxYear().taxYear;

  const [selectedRows, setSelectedRows] = useState<IPensionSubmission[]>([]);
  const [isOpenFilterModal, setIsOpenFilterModal] = useState(false);

  useEffect(() => {
    handleFetchData({
      page: 0,
      size: 20,
      taxYear: currentTaxYear,
    });
  }, []);

  const handleFetchData = (
    params: GetPendingPensionContributionReportListParams,
  ) => {
    const newParams = {
      ...params,
      reportType: 'pension',
      isPendingSubmitPension: true,
      paymentModels: [
        PAYMENT_MODEL_ENUM.UMBRELLA,
        PAYMENT_MODEL_ENUM.PAYEE,
        PAYMENT_MODEL_ENUM.HYBRID,
        PAYMENT_MODEL_ENUM.PAYE_HYBRID,
        PAYMENT_MODEL_ENUM.PAYE_FTH,
      ],
    };

    dispatch(reportActions.getPendingPensionContributionReportList(newParams));
  };

  const refetch = () => {
    handleFetchData({
      ...queryParams,
    });
    cbAfterAction();
  };

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const handleSearch = useCallback(
    debounce(
      (e: React.ChangeEvent<HTMLInputElement>) =>
        handleFetchData({ ...queryParams, search: e.target.value }),
      500,
    ),
    [queryParams],
  );

  const handleFilter = (values: PensionContributionFilterValues) => {
    const params: GetPendingPensionContributionReportListParams = {
      ...queryParams,
      payFrequency: values.payFrequency,
      taxPeriodFrom: values.taxPeriodFrom,
      taxPeriodTo: values.taxPeriodTo,
    };
    handleFetchData(params);
  };

  const {
    run: submitPensionContributionReport,
    loading: submitPensionContributionReportLoading,
  } = useSubmitPensionContributionReport();
  const submitPensionByPayrollSelected = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    submitPensionContributionReport({
      ids: selectedRows.map((item) => item.id),
    }).then((res) => {
      if (res.success) {
        showMessage({
          type: 'success',
          content:
            messages['report.pension.submitPensionContributionReportSuccess'],
        });
      } else {
        showMessage({
          type: 'error',
          content:
            res.message ||
            messages['report.pension.submitPensionContributionReportError'],
        });
      }
      refetch();
    });
  };

  return (
    <AppModalContainer
      title="Pension Contributions"
      isOpen={isOpen}
      toggle={toggle}
      hiddenFooter
      className="max-sm:w-[100vw]"
      width={1200}
      minHeight="0"
    >
      {submitPensionContributionReportLoading && <AppLoader />}
      <div className="flex justify-between flex-wrap w-full gap-4 mb-[20px]">
        <div className="flex flex-wrap gap-4">
          <AppInputSearch onChange={handleSearch} />
          <AppMenuFilter
            options={[]}
            onChange={() => {}}
            onClick={() => setIsOpenFilterModal(true)}
          />
        </div>
        <div className="flex flex-wrap gap-4 items-end">
          <TaxYearSelect
            initValue={currentTaxYear}
            onFilter={(value) =>
              handleFetchData({ ...queryParams, taxYear: value.taxYear })
            }
          />
          <AppButton name="Submit" onClick={submitPensionByPayrollSelected} />
        </div>
      </div>
      <AppsContent>
        <AppTableContainer
          rowKey="id"
          data={list ?? []}
          loading={loading}
          page={pagination.current}
          total={pagination.totalDocs}
          pageSize={pagination.pageSize}
          scroll={{ x: 2700, y: 700 }}
          columns={getPendingPensionContributionReportColumns({
            messages,
            payrollCountry,
          })}
          onChangePagination={onChangePagination}
          rowSelection={{
            type: 'checkbox',
            onChange: (_: React.Key[], selectedRows: any[]) => {
              setSelectedRows(selectedRows);
            },
          }}
        />
      </AppsContent>
      <PensionContributionFilterModal
        isOpen={isOpenFilterModal}
        toggle={() => setIsOpenFilterModal(false)}
        onFilter={handleFilter}
      />
    </AppModalContainer>
  );
};

export default PendingPensionSubmissionModal;
